// src/modules/app-views/account/pages/Init/modals/ChangePhoneModal/Step3.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Alert, Typography } from 'antd';
import { MobileOutlined, NumberOutlined, WhatsAppOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import appService from 'services/appService';
import authService from 'services/authService';
import translateX from 'utils/translateX';
import { getPhoneMeta } from 'utils/phone';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const VerifyNewPhoneStepContent = ({
  storedUser,
  newPhoneDataRef,
  codeSent,
  setCodeSent,
  isWhatsApp,
  setIsWhatsApp,
}) => {
  const [isSending, setIsSending] = useState(false);

  const newPhone = newPhoneDataRef.current.phone;
  const newPhoneCountryCode = newPhoneDataRef.current.countryCode;
  const phoneMeta = getPhoneMeta(newPhone, newPhoneCountryCode);

  // Send code automatically when step opens
  useEffect(() => {
    if (!codeSent && newPhone && !isSending) {
      sendVerificationCode();
    }
  }, [codeSent, newPhone]);

  const sendVerificationCode = async () => {
    if (isSending) return;

    setIsSending(true);

    try {
      const result = await authService.sendSmsRegistrationCode({
        name: storedUser?.name,
        mobile: newPhone,
        mobileCountryCode: newPhoneCountryCode,
      });

      if (result?.success) {
        const channel = result?.channel || 'sms';
        setIsWhatsApp(channel === 'whatsapp');
        setCodeSent(true);

        appService.notification({
          key: 'newPhoneCodeSentKey',
          flag: 'info',
          title: phoneMeta?.national || newPhone,
          description: channel === 'whatsapp'
            ? 'verification_code_sent_whatsapp'
            : 'verification_code_sent_sms',
          duration: 0,
          icon: channel === 'whatsapp'
            ? <WhatsAppOutlined style={{ color: '#25D366' }} />
            : <MobileOutlined />,
        });
      } else {
        await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
      }
    } catch (err) {
      console.error('[ChangePhoneModal] Error sending code:', err);
      await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={isWhatsApp ? <WhatsAppOutlined style={{ color: '#25D366' }} /> : <MobileOutlined />}
        title={translateX('verify_new_phone')}
        description={translateX('verify_new_phone_description', {
          '%PHONE%': phoneMeta?.national || newPhone,
        })}
        style={{ marginBottom: 16 }}
      />

      <SimpleInput
        name="newPhoneCode"
        label="verification_code"
        minLength={4}
        maxLength={4}
        required
        prefix={<NumberOutlined className="text-primary" />}
        placeholder="0000"
      />

      {codeSent && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translateX('didnt_receive_code')}{' '}
          <a onClick={sendVerificationCode} style={{ cursor: isSending ? 'wait' : 'pointer' }}>
            {translateX('resend_code')}
          </a>
        </Text>
      )}
    </>
  );
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
const validateNewPhoneCode = async ({
  form,
  setLoading,
  goFocus,
  newPhoneDataRef,
}) => {
  const code = form?.getFieldValue('newPhoneCode');
  const newPhone = newPhoneDataRef.current.phone;
  const newPhoneCountryCode = newPhoneDataRef.current.countryCode;

  if (!code || code.trim() === '') {
    await appService.message({ flag: 'w', text: 'verification_code_required' });
    goFocus?.('newPhoneCode', true);
    return false;
  }

  try {
    setLoading(true);

    const success = await authService.validateSmsCode({
      mobile: newPhone,
      mobileCountryCode: newPhoneCountryCode,
      mobileCode: code,
    });

    if (success) {
      appService.notificationDestroy('newPhoneCodeSentKey');
      return true;
    } else {
      await appService.message({ flag: 'w', text: 'invalid_verification_code' });
      goFocus?.('newPhoneCode', true);
      return false;
    }
  } catch (err) {
    console.error('[ChangePhoneModal] Error validating code:', err);
    await appService.message({ flag: 'e', text: 'verification_failed' });
    goFocus?.('newPhoneCode', true);
    return false;
  } finally {
    setLoading(false);
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const verifyNewPhoneStep = ({ storedUser, newPhoneDataRef }) => {
  let codeSent = false;
  let isWhatsApp = false;

  return {
    stepTitle: 'verification',
    stepTooltip: 'verify_new_phone',
    content: ({ form, goFocus }) => {
      const [localCodeSent, setLocalCodeSent] = useState(codeSent);
      const [localIsWhatsApp, setLocalIsWhatsApp] = useState(isWhatsApp);

      // Sync with closure
      useEffect(() => {
        codeSent = localCodeSent;
        isWhatsApp = localIsWhatsApp;
      }, [localCodeSent, localIsWhatsApp]);

      return (
        <VerifyNewPhoneStepContent
          storedUser={storedUser}
          newPhoneDataRef={newPhoneDataRef}
          codeSent={localCodeSent}
          setCodeSent={setLocalCodeSent}
          isWhatsApp={localIsWhatsApp}
          setIsWhatsApp={setLocalIsWhatsApp}
        />
      );
    },
    validationFields: ['newPhoneCode'],
    nextButtonText: 'change_phone',
    nextButtonIcon: <MobileOutlined />,
    isConfirmStep: true,
    onNext: async ({ form, setLoading, goFocus }) =>
      validateNewPhoneCode({ form, setLoading, goFocus, newPhoneDataRef }),
    onEnter: ({ goFocus }) => {
      setTimeout(() => goFocus?.('newPhoneCode'), 100);
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default verifyNewPhoneStep;
